// src/pages/Navbar.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogoutModal } from "../components/LogoutModal";
import config from "../config";
import SocialIcon from "../components/SocialIcon";

const TOP_LEVEL_ROUTES = new Set([
  "portfolio", "products", "blog", "testimonials", "about-us", "contact-us",
]);

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function withSlash(link = "") {
  return link.startsWith("/") ? link : `/${link}`;
}

const DROPDOWN_IMAGES = [
  `${UPLOADS_URL}/navbar/navbar_01.jpeg`,
  `${UPLOADS_URL}/navbar/navbar_02.jpeg`,
  `${UPLOADS_URL}/navbar/navbar_03.jpeg`,
];

/* ─────────────────────────────────────────
   Dropdown
───────────────────────────────────────── */
function Dropdown({ items, open, onClose, imageIndex }) {
  const imgSrc = DROPDOWN_IMAGES[imageIndex] ?? DROPDOWN_IMAGES[0];
  return (
    <div className={`nd ${open ? "nd--open" : ""}`} role="none">
      <div className="nd__bridge" aria-hidden="true" />
      <div className="nd__inner">
        <ul className="nd__list" role="menu">
          {items.map((c) => (
            <li key={c.id} role="none">
              <a
                href={TOP_LEVEL_ROUTES.has(c.link) ? `/${c.link}` : `/services/${c.link}`}
                className="nd__link" role="menuitem" onClick={onClose}
              >
                <svg className="nd__link-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {c.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="nd__img-wrap" aria-hidden="true">
          <img src={imgSrc} alt="" className="nd__img"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          <div className="nd__img-fade" />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   NavItem
───────────────────────────────────────── */
function NavItem({ item, active, dropdownIndex }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const hasSub = item.children?.length > 0;

  // Render shop link as a red button
  const isShop = item.link === "shop";

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleMouseEnter = () => { clearTimeout(closeTimer.current); if (hasSub) setOpen(true); };
  const handleMouseLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };

  if (isShop) {
    return (
      <li ref={ref} className="ni">
        <a href={withSlash(item.link)} className="ni__shop-btn">
          {item.label}
        </a>
      </li>
    );
  }

  return (
    <li ref={ref} className="ni" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {hasSub ? (
        <button type="button"
          className={`ni__link ${active ? "ni__link--active" : ""}`}
          aria-haspopup="true" aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{ border: "none", background: "transparent", cursor: "pointer" }}
        >
          {item.label}
          <svg className={`ni__chev ${open ? "ni__chev--open" : ""}`}
            width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <path d="M1.5 3.5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : (
        <a href={withSlash(item.link)} className={`ni__link ${active ? "ni__link--active" : ""}`}>
          {item.label}
        </a>
      )}
      {hasSub && (
        <div onMouseEnter={() => clearTimeout(closeTimer.current)} onMouseLeave={handleMouseLeave}>
          <Dropdown items={item.children} open={open} onClose={() => setOpen(false)} imageIndex={dropdownIndex} />
        </div>
      )}
    </li>
  );
}

/* ─────────────────────────────────────────
   Account dropdown
───────────────────────────────────────── */
function AccountDropdown({ customer, onRequestLogout, onClose }) {
  return (
    <div className="accd">
      <div className="accd__header">
        <div className="accd__avatar">{customer?.first_name?.[0]?.toUpperCase() ?? "U"}</div>
        <div className="accd__info">
          <span className="accd__name">{customer?.first_name} {customer?.last_name}</span>
          <span className="accd__email">{customer?.email}</span>
        </div>
      </div>
      <ul className="accd__links">
        <li>
          <a href="/account" className="accd__link" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            My Account
          </a>
        </li>
        <li>
          <a href="/account?tab=orders" className="accd__link" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
            </svg>
            My Orders
          </a>
        </li>
        <li>
          <a href="/wishlist" className="accd__link" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            Wishlist
          </a>
        </li>
      </ul>
      <div className="accd__footer">
        <button className="accd__logout" onClick={() => { onClose(); onRequestLogout(); }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Mobile Drawer
───────────────────────────────────────── */
function Drawer({ tree, socialLinks, open, onClose, isLoggedIn, customer, cartCount, onOpenAuth, onOpenCart, onRequestLogout }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div className={`dov ${open ? "dov--open" : ""}`} onClick={onClose} aria-hidden="true" />
      <nav className={`dr ${open ? "dr--open" : ""}`} aria-label="Mobile navigation">
        <div className="dr__head">
          <span className="dr__brand">Menu</span>
          <button className="dr__x" onClick={onClose} aria-label="Close menu">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="dr__auth">
          {isLoggedIn ? (
            <div className="dr__user">
              <div className="dr__avatar">{customer?.first_name?.[0]?.toUpperCase() ?? "U"}</div>
              <div className="dr__user-info">
                <span className="dr__user-name">{customer?.first_name} {customer?.last_name}</span>
                <span className="dr__user-email">{customer?.email}</span>
              </div>
            </div>
          ) : (
            <button className="dr__signin-btn" onClick={() => { onOpenAuth("login"); onClose(); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              Sign in / Register
            </button>
          )}
          <button className="dr__cart-btn" onClick={() => { onOpenCart(); onClose(); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            {cartCount > 0 && <span className="dr__cart-badge">{cartCount}</span>}
          </button>
        </div>

        <ul className="dr__list">
          {tree.map((item) => {
            const hasSub = item.children?.length > 0;
            return (
              <li key={item.id} className="dr__item">
                <div className="dr__row">
                  {hasSub ? (
                    <span className="dr__link dr__link--parent" onClick={() => toggle(item.id)}>{item.label}</span>
                  ) : (
                    <a href={withSlash(item.link)} className="dr__link" onClick={onClose}>{item.label}</a>
                  )}
                  {hasSub && (
                    <button className={`dr__tog ${expanded[item.id] ? "dr__tog--open" : ""}`}
                      onClick={() => toggle(item.id)} aria-label={`Toggle ${item.label}`}>
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                        <path d="M2 4.5l4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>
                {hasSub && expanded[item.id] && (
                  <ul className="dr__sub">
                    {item.children.map((c) => (
                      <li key={c.id}>
                        <a href={TOP_LEVEL_ROUTES.has(c.link) ? `/${c.link}` : `/services/${c.link}`}
                          className="dr__sub-link" onClick={onClose}>{c.label}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
          {isLoggedIn && (
            <>
              <li className="dr__item"><a href="/account" className="dr__link" onClick={onClose}>My Account</a></li>
              <li className="dr__item"><a href="/wishlist" className="dr__link" onClick={onClose}>Wishlist</a></li>
              <li className="dr__item">
                <button className="dr__link dr__link--logout" onClick={() => { onClose(); onRequestLogout(); }}>
                  Sign out
                </button>
              </li>
            </>
          )}
        </ul>

        {socialLinks.length > 0 && (
          <div className="dr__social">
            <span className="dr__follow">Follow Us:</span>
            {socialLinks.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                className="dr__soc-link" aria-label={s.platform}>
                <SocialIcon platform={s.platform} />
              </a>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}

/* ─────────────────────────────────────────
   InfoPanel
───────────────────────────────────────── */
function InfoPanel({ open, onClose, logo, contact, socialLinks, aboutUs }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div className={`ipov ${open ? "ipov--open" : ""}`} onClick={onClose} aria-hidden="true" />
      <aside className={`ip ${open ? "ip--open" : ""}`} aria-label="Site information panel">
        <button className="ip__close" onClick={onClose} aria-label="Close panel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        {logo && (
          <div className="ip__logo">
            <img src={`${UPLOADS_URL}/logos/${logo.image_filename}`} alt={logo.alt_text || logo.title || "Logo"} />
          </div>
        )}
        <div className="ip__section">
          <h3 className="ip__heading">About Us</h3>
          <p className="ip__text">
            {aboutUs?.content
              ? aboutUs.content.substring(0, 220) + (aboutUs.content.length > 220 ? "…" : "")
              : "We are a professional company dedicated to delivering quality services and solutions to our clients."}
          </p>
        </div>
        {contact && (
          <div className="ip__section">
            <h3 className="ip__heading">Contact Info</h3>
            <ul className="ip__contact">
              {contact.email_address && (
                <li className="ip__contact-item">
                  <span className="ip__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                    </svg>
                  </span>
                  <div>
                    <span className="ip__label">Email</span>
                    <a href={`mailto:${contact.email_address}`} className="ip__value">{contact.email_address}</a>
                  </div>
                </li>
              )}
              {contact.phone_number && (
                <li className="ip__contact-item">
                  <span className="ip__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                    </svg>
                  </span>
                  <div>
                    <span className="ip__label">Phone</span>
                    <a href={`tel:${contact.phone_number}`} className="ip__value">{contact.phone_number}</a>
                  </div>
                </li>
              )}
              {contact.physical_address && (
                <li className="ip__contact-item">
                  <span className="ip__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                    </svg>
                  </span>
                  <div>
                    <span className="ip__label">Address</span>
                    <span className="ip__value">{contact.physical_address}</span>
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
        {socialLinks.length > 0 && (
          <div className="ip__section">
            <h3 className="ip__heading">Follow Us</h3>
            <div className="ip__social">
              {socialLinks.map((s) => (
                <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                  className="ip__soc-link" aria-label={s.platform}>
                  <SocialIcon platform={s.platform} />
                </a>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

/* ═════════════════════════════════════════
   MAIN Navbar
═════════════════════════════════════════ */
function Navbar({ isLoggedIn = false, customer = null, cartItems = [], onOpenCart, onOpenAuth, onLogout }) {
  const navigate = useNavigate();
  const [logo,       setLogo]       = useState(null);
  const [tree,       setTree]       = useState([]);
  const [social,     setSocial]     = useState([]);
  const [contact,    setContact]    = useState(null);
  const [aboutUs,    setAboutUs]    = useState(null);
  const [scrolled,   setScrolled]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [infoOpen,   setInfoOpen]   = useState(false);
  const [acctOpen,   setAcctOpen]   = useState(false);
  const [ready,      setReady]      = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const acctRef = useRef(null);

  const cartCount   = cartItems.reduce((s, i) => s + (i.qty || 0), 0);
  const currentPath = window.location.pathname;
  const navSocial   = social.filter(s => {
    const show = s.show_in_nav;
    return show === 1 || show === "1" || show === true;
  });

  // Find shop link label from tree (fallback to "SHOP")
  const shopItem = tree.find(item => item.link === "shop");
  const shopLabel = shopItem?.label ?? "SHOP";

  function handleRequestLogout() {
    setAcctOpen(false);
    setMobileOpen(false);
    setShowLogout(true);
  }

  function handleLogoutConfirm() {
    onLogout();
    setShowLogout(false);
    navigate("/shop");
  }

  useEffect(() => {
    const h = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    Promise.all([
      fetchJSON(`${API_BASE}/logo.php`),
      fetchJSON(`${API_BASE}/navbar.php`),
      fetchJSON(`${API_BASE}/social-links.php`),
      fetchJSON(`${API_BASE}/company-contact.php`),
    ])
      .then(([logoData, navData, socialData, contactData]) => {
        setLogo(logoData);
        const all = navData?.items ?? (Array.isArray(navData) ? navData : []);
        const roots = all
          .filter(n => !n.parent_id || n.parent_id === 0)
          .sort((a, b) => a.order - b.order)
          .map(root => ({
            ...root,
            children: all
              .filter(c => String(c.parent_id) === String(root.id))
              .sort((a, b) => a.order - b.order),
          }));
        setTree(roots);
        setSocial(Array.isArray(socialData) ? socialData : []);
        setContact(contactData);
      })
      .catch(err => console.error("Navbar load error:", err))
      .finally(() => setReady(true));

    fetchJSON(`${API_BASE}/about.php`).then(setAboutUs).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 55);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  let dropdownCounter = 0;

  return (
    <>
      {/* ── Logout confirmation modal ── */}
      {showLogout && (
        <LogoutModal
          onConfirm={handleLogoutConfirm}
          onCancel={() => setShowLogout(false)}
        />
      )}

      <header className="nb-shell">
        <div className={`nb-wrap ${scrolled ? "nb-wrap--scrolled" : ""} ${!ready ? "nb-wrap--loading" : ""}`}>
          <div className="nb-inner">

            {/* Logo */}
            <a href="/" className="nb-logo" aria-label="Homepage">
              {logo ? (
                <img src={`${UPLOADS_URL}/logos/${logo.image_filename}`}
                  alt={logo.alt_text || logo.title || "Logo"}
                  className="nb-logo__img"
                  onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }} />
              ) : null}
              <span className="nb-logo__text" style={logo ? { display: "none" } : {}}>Vybertech</span>
            </a>

            <div className="nb-right">
              {/* Top strip */}
              <div className="nb-top">
                <div className="nb-top__left">
                  {navSocial.length > 0 && (
                    <>
                      <span className="nb-top__follow">Follow Us:</span>
                      {navSocial.map(s => (
                        <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                          className="nb-top__soc" aria-label={s.platform}>
                          <SocialIcon platform={s.platform} />
                        </a>
                      ))}
                    </>
                  )}
                </div>
                <div className="nb-top__right">
                  {contact?.email_address && (
                    <a href={`mailto:${contact.email_address}`} className="nb-top__info nb-top__email">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      {contact.email_address}
                    </a>
                  )}
                  {contact?.phone_number && (
                    <a href={`tel:${contact.phone_number}`} className="nb-top__info nb-top__phone">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.18 6.18l1.27-.34a2 2 0 012.11.45c.907.34 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                      {contact.phone_number}
                    </a>
                  )}
                  <span className="nb-top__info nb-top__info--static">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    Mon – Fri (08AM – 05PM)
                  </span>
                </div>
              </div>

              {/* Main nav row */}
              <div className="nb-nav">
                <nav className="nb-nav__links" aria-label="Main navigation">
                  <ul className="nb-nav__list">
                    {tree.map(item => {
                      const hasSub = item.children?.length > 0;
                      const idx = hasSub ? dropdownCounter++ : -1;
                      return (
                        <NavItem key={item.id} item={item}
                          active={currentPath === withSlash(item.link) || currentPath.startsWith(withSlash(item.link) + "/")}
                          dropdownIndex={idx}
                        />
                      );
                    })}
                  </ul>
                </nav>

                <div className="nb-nav__end">
                  <a href="/contact-us" className="nb-nav__cta">
                    BOOK
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                    </svg>
                  </a>

                  {/* Cart */}
                  <button className="nb-nav__icon-btn" onClick={onOpenCart} aria-label="Open cart">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                    </svg>
                    {cartCount > 0 && <span className="nb-nav__badge">{cartCount}</span>}
                  </button>

                  {/* Account */}
                  <div className="nb-acct" ref={acctRef}>
                    {isLoggedIn ? (
                      <>
                        <button
                          className="nb-nav__icon-btn nb-nav__icon-btn--acct"
                          onClick={() => setAcctOpen(v => !v)}
                          aria-label="Account menu"
                          aria-expanded={acctOpen}
                        >
                          <span className="nb-acct__initials">
                            {customer?.first_name?.[0]?.toUpperCase() ?? "U"}
                          </span>
                        </button>
                        {acctOpen && (
                          <AccountDropdown
                            customer={customer}
                            onRequestLogout={handleRequestLogout}
                            onClose={() => setAcctOpen(false)}
                          />
                        )}
                      </>
                    ) : (
                      <button className="nb-nav__icon-btn" onClick={() => onOpenAuth("login")} aria-label="Sign in">
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  <button className="nb-nav__info-btn" onClick={() => setInfoOpen(true)} aria-label="Open info panel">
                    <span className="bline"/><span className="bline"/><span className="bline bline--short"/>
                  </button>

                  {/* ── Mobile-only: SHOP + BOOK buttons ── */}
                  <a href="/shop" className="nb-nav__shopnow">{shopLabel}</a>
                  <a href="/contact-us" className="nb-nav__booknow">Book Now</a>

                  <button className="nb-nav__burger" onClick={() => setMobileOpen(true)} aria-label="Open menu" aria-expanded={mobileOpen}>
                    <span className="bline"/><span className="bline"/><span className="bline bline--short"/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="nb-spacer" aria-hidden="true" />

      <Drawer
        tree={tree}
        socialLinks={social}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        isLoggedIn={isLoggedIn}
        customer={customer}
        cartCount={cartCount}
        onOpenAuth={onOpenAuth}
        onOpenCart={onOpenCart}
        onRequestLogout={handleRequestLogout}
      />
      <InfoPanel
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        logo={logo}
        contact={contact}
        socialLinks={social}
        aboutUs={aboutUs}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        a{text-decoration:none} ul{list-style:none}
        body,.nb-shell *{font-family:'Roboto',sans-serif}

        .nb-shell{position:fixed;top:0;left:0;right:0;z-index:1000}
        .nb-spacer{height:96px}
        .nb-wrap{background:#013d97;transition:box-shadow .3s}
        .nb-wrap--scrolled{box-shadow:0 4px 24px rgba(0,0,0,.18)}
        .nb-wrap--loading{pointer-events:none;opacity:.8}
        .nb-inner{display:flex;align-items:stretch;max-width:1280px;margin:0 auto;width:100%}

        .nb-logo{flex-shrink:0;width:200px;background:#029add;display:flex;align-items:center;justify-content:center;padding:0 8px 0 12px;position:relative;z-index:2;overflow:visible}
        .nb-logo::after{content:"";position:absolute;right:-22px;top:0;bottom:0;width:44px;background:#029add;clip-path:polygon(0 0,0 100%,100% 100%)}
        .nb-logo__img{height:90px;width:auto;max-width:170px;object-fit:contain;display:block;position:relative;z-index:1}
        .nb-logo__text{font-size:22px;font-weight:800;color:#fff;letter-spacing:-.5px;position:relative;z-index:1}
        .nb-right{flex:1;display:flex;flex-direction:column;min-width:0}

        .nb-top{height:40px;background:#013d97;display:flex;align-items:center;justify-content:space-between;padding:0 24px 0 36px;border-bottom:1px solid rgba(255,255,255,.1)}
        .nb-top__left{display:flex;align-items:center;gap:10px}
        .nb-top__follow{font-size:12px;font-weight:600;color:#fff;white-space:nowrap}
        .nb-top__soc{color:#fff;opacity:.85;display:flex;align-items:center;transition:opacity .2s}
        .nb-top__soc:hover{opacity:1}
        .nb-top__right{display:flex;align-items:center;gap:20px}
        .nb-top__info{display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(255,255,255,.85);white-space:nowrap;transition:color .2s}
        .nb-top__info:hover{color:#fff}
        .nb-top__info--static{color:rgba(255,255,255,.65);cursor:default}
        .nb-top__info svg{opacity:.8;flex-shrink:0}

        .nb-nav{height:56px;background:#fff;display:flex;align-items:center;padding:0 24px 0 36px;gap:12px}
        .nb-nav__links{flex:1}
        .nb-nav__list{display:flex;align-items:center;gap:2px}
        .nb-nav__end{display:flex;align-items:center;gap:8px;flex-shrink:0}

        .ni{position:relative}
        .ni__link{display:flex;align-items:center;gap:4px;padding:6px 12px;font-size:14px;font-weight:600;color:#013d97;border-radius:4px;white-space:nowrap;transition:color .2s}
        .ni__link:hover,.ni__link--active{color:#029add}
        .ni__chev{opacity:.55;transition:transform .22s;flex-shrink:0}
        .ni__chev--open{transform:rotate(180deg)}

        /* ── Desktop: Shop nav button (red) ── */
        .ni__shop-btn{display:inline-flex;align-items:center;padding:6px 14px;background:#dc2626;color:#fff;font-size:13.5px;font-weight:700;border-radius:6px;white-space:nowrap;transition:background .2s,transform .15s;letter-spacing:.02em;text-transform:uppercase}
        .ni__shop-btn:hover{background:#b91c1c;transform:translateY(-1px)}

        .nd{position:absolute;top:100%;left:0;background:#fff;border:1px solid rgba(0,0,0,.08);border-top:3px solid #029add;border-radius:0 0 10px 10px;min-width:520px;opacity:0;visibility:hidden;pointer-events:none;transform:translateY(-6px);transition:opacity .22s,transform .22s,visibility .22s;box-shadow:0 16px 40px rgba(0,0,0,.13);overflow:hidden}
        .nd--open{opacity:1;visibility:visible;pointer-events:auto;transform:translateY(0)}
        .nd__bridge{position:absolute;top:-12px;left:0;right:0;height:12px}
        .nd__inner{display:flex;align-items:stretch}
        .nd__list{flex:0 0 220px;padding:10px 0;border-right:1px solid rgba(0,0,0,.06)}
        .nd__link{display:flex;align-items:center;gap:10px;padding:11px 20px;font-size:13.5px;font-weight:600;color:#013d97;transition:color .18s,background .18s,padding-left .18s;white-space:nowrap}
        .nd__link:hover{color:#029add;background:#f0f8fd;padding-left:26px}
        .nd__link-arrow{flex-shrink:0;opacity:.45;transition:opacity .18s,transform .18s}
        .nd__link:hover .nd__link-arrow{opacity:1;transform:translateX(3px)}
        .nd__img-wrap{flex:1;position:relative;overflow:hidden;min-height:180px}
        .nd__img{width:100%;height:100%;object-fit:cover;object-position:center;display:block}
        .nd__img-fade{position:absolute;inset:0;background:linear-gradient(to left,#fff 0%,rgba(255,255,255,0) 55%);pointer-events:none}

        .nb-nav__icon-btn{position:relative;display:flex;align-items:center;justify-content:center;width:38px;height:38px;background:#f0f4fa;border:1.5px solid #e2e8f0;border-radius:8px;color:#013d97;cursor:pointer;transition:background .18s,border-color .18s,color .18s;flex-shrink:0;}
        .nb-nav__icon-btn:hover{background:#013d97;border-color:#013d97;color:#fff}
        .nb-nav__icon-btn--acct{background:#013d97;border-color:#013d97;color:#fff;padding:0}
        .nb-nav__icon-btn--acct:hover{background:#029add;border-color:#029add}
        .nb-acct__initials{font-size:14px;font-weight:800;color:#fff;line-height:1;pointer-events:none;}
        .nb-nav__badge{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;background:#ef4444;color:#fff;font-size:10px;font-weight:800;border-radius:9px;display:flex;align-items:center;justify-content:center;padding:0 4px;border:2px solid #fff;pointer-events:none;}

        .nb-acct{position:relative}
        .accd{position:absolute;top:calc(100% + 10px);right:0;background:#fff;border:1px solid #e8ecf3;border-radius:14px;width:260px;box-shadow:0 8px 32px rgba(1,15,50,.14),0 2px 8px rgba(1,15,50,.08);z-index:2000;overflow:hidden;animation:accd-in .18s ease;}
        @keyframes accd-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        .accd__header{display:flex;align-items:center;gap:12px;padding:16px;background:linear-gradient(135deg,#013d97 0%,#0255cc 100%)}
        .accd__avatar{width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;color:#fff;flex-shrink:0;border:2px solid rgba(255,255,255,.3)}
        .accd__info{display:flex;flex-direction:column;gap:2px;min-width:0}
        .accd__name{font-size:13.5px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .accd__email{font-size:11px;color:rgba(255,255,255,.7);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .accd__links{padding:8px 0}
        .accd__link{display:flex;align-items:center;gap:10px;padding:10px 16px;font-size:13.5px;font-weight:600;color:#374151;transition:background .15s,color .15s}
        .accd__link:hover{background:#f0f4fa;color:#013d97}
        .accd__link svg{color:#6b7a99;flex-shrink:0;transition:color .15s}
        .accd__link:hover svg{color:#013d97}
        .accd__footer{padding:8px 16px 14px;border-top:1px solid #f0f3f8}
        .accd__logout{display:flex;align-items:center;gap:10px;width:100%;padding:9px 0;background:none;border:none;cursor:pointer;font-family:'Roboto',sans-serif;font-size:13.5px;font-weight:600;color:#dc2626;transition:color .15s}
        .accd__logout:hover{color:#b91c1c}
        .accd__logout svg{flex-shrink:0}

        .nb-nav__cta{display:inline-flex;align-items:center;gap:6px;padding:8px 18px;background:#029add;color:#fff;font-size:13.5px;font-weight:700;border-radius:6px;white-space:nowrap;transition:background .2s,transform .15s}
        .nb-nav__cta:hover{background:#0180b8;transform:translateY(-1px)}
        .nb-nav__info-btn{display:flex;flex-direction:column;justify-content:center;align-items:center;gap:5px;width:38px;height:38px;background:#013d97;border:none;border-radius:6px;cursor:pointer;padding:9px;transition:background .2s;flex-shrink:0}
        .nb-nav__info-btn:hover{background:#029add}

        /* ── Mobile-only SHOP button (page default = white text on #013d97) ── */
        .nb-nav__shopnow{display:none;align-items:center;justify-content:center;padding:0 16px;height:38px;background:#013d97;border:2px solid rgba(255,255,255,.35);color:#fff;font-size:13px;font-weight:800;border-radius:6px;white-space:nowrap;letter-spacing:.03em;text-transform:uppercase;transition:background .2s,border-color .2s;flex-shrink:0}
        .nb-nav__shopnow:hover{background:#0255cc;border-color:rgba(255,255,255,.6)}

        /* ── Mobile-only BOOK button ── */
        .nb-nav__booknow{display:none;align-items:center;justify-content:center;padding:0 16px;height:38px;background:#ff8c00;color:#fff;font-size:13px;font-weight:800;border-radius:6px;white-space:nowrap;letter-spacing:.03em;text-transform:uppercase;transition:background .2s;flex-shrink:0}
        .nb-nav__booknow:hover{background:#e07a00}

        .nb-nav__burger{display:none;flex-direction:column;justify-content:center;align-items:center;gap:5px;width:38px;height:38px;background:#013d97;border:none;border-radius:6px;cursor:pointer;padding:9px;transition:background .2s}
        .nb-nav__burger:hover{background:#029add}
        .bline{display:block;width:18px;height:2px;background:#fff;border-radius:2px}
        .bline--short{width:12px}

        .dov{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:1100;opacity:0;visibility:hidden;transition:opacity .3s,visibility .3s}
        .dov--open{opacity:1;visibility:visible}
        .dr{position:fixed;top:0;right:0;bottom:0;width:min(320px,88vw);background:#fff;z-index:1200;display:flex;flex-direction:column;transform:translateX(100%);transition:transform .34s cubic-bezier(.4,0,.2,1);overflow-y:auto}
        .dr--open{transform:translateX(0)}
        .dr__head{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;background:#013d97}
        .dr__brand{font-size:15px;font-weight:700;color:#fff}
        .dr__x{display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:rgba(255,255,255,.12);border:none;border-radius:4px;color:#fff;cursor:pointer;transition:background .2s}
        .dr__x:hover{background:#029add}
        .dr__auth{display:flex;align-items:center;justify-content:space-between;padding:12px 22px;background:#f7f9fc;border-bottom:1px solid #e8ecf3;gap:10px}
        .dr__user{display:flex;align-items:center;gap:10px;flex:1;min-width:0}
        .dr__avatar{width:36px;height:36px;border-radius:50%;background:#013d97;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:#fff;flex-shrink:0}
        .dr__user-info{display:flex;flex-direction:column;min-width:0}
        .dr__user-name{font-size:13px;font-weight:700;color:#0f1c38;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .dr__user-email{font-size:11px;color:#8895b0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .dr__signin-btn{display:flex;align-items:center;gap:8px;flex:1;padding:8px 12px;background:#013d97;color:#fff;border:none;border-radius:8px;font-family:'Roboto',sans-serif;font-size:13px;font-weight:700;cursor:pointer;transition:background .18s}
        .dr__signin-btn:hover{background:#029add}
        .dr__cart-btn{position:relative;display:flex;align-items:center;justify-content:center;width:38px;height:38px;background:#fff;border:1.5px solid #e2e8f0;border-radius:8px;color:#013d97;cursor:pointer;flex-shrink:0;transition:background .15s}
        .dr__cart-btn:hover{background:#f0f4fa}
        .dr__cart-badge{position:absolute;top:-5px;right:-5px;min-width:16px;height:16px;background:#ef4444;color:#fff;font-size:9px;font-weight:800;border-radius:8px;display:flex;align-items:center;justify-content:center;padding:0 3px;border:2px solid #f7f9fc}
        .dr__list{flex:1;padding:8px 0}
        .dr__item{border-bottom:1px solid #f0f0f0}
        .dr__row{display:flex;align-items:center;justify-content:space-between}
        .dr__link{display:block;flex:1;padding:13px 22px;font-size:14.5px;font-weight:600;color:#013d97;transition:color .2s;background:none;border:none;font-family:'Roboto',sans-serif;cursor:pointer;text-align:left;width:100%}
        .dr__link:hover{color:#029add}
        .dr__link--parent{cursor:pointer;user-select:none}
        .dr__link--logout{color:#dc2626;font-size:13.5px}
        .dr__link--logout:hover{color:#b91c1c}
        .dr__tog{background:transparent;border:none;color:#013d97;cursor:pointer;padding:8px 14px;transition:color .2s,transform .22s}
        .dr__tog:hover{color:#029add}
        .dr__tog--open{transform:rotate(180deg)}
        .dr__sub{background:#fafafa}
        .dr__sub-link{display:block;padding:10px 22px 10px 34px;font-size:13.5px;font-weight:500;color:#424244;border-bottom:1px solid #f0f0f0;transition:color .2s}
        .dr__sub-link:hover{color:#029add}
        .dr__social{display:flex;align-items:center;gap:10px;padding:14px 22px;background:#013d97;position:sticky;bottom:0;z-index:1}
        .dr__follow{font-size:12px;font-weight:600;color:rgba(255,255,255,.7);margin-right:4px}
        .dr__soc-link{width:32px;height:32px;border-radius:4px;background:rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;transition:background .2s}
        .dr__soc-link:hover{background:#029add}

        .ipov{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:1300;opacity:0;visibility:hidden;transition:opacity .3s,visibility .3s}
        .ipov--open{opacity:1;visibility:visible}
        .ip{position:fixed;top:0;right:0;bottom:0;width:min(360px,92vw);background:#fff;z-index:1400;padding:36px 32px;display:flex;flex-direction:column;gap:28px;transform:translateX(100%);transition:transform .35s cubic-bezier(.4,0,.2,1);overflow-y:auto}
        .ip--open{transform:translateX(0)}
        .ip__close{position:absolute;top:20px;right:20px;width:36px;height:36px;border-radius:50%;background:#029add;border:none;display:flex;align-items:center;justify-content:center;color:#fff;cursor:pointer;transition:background .2s,transform .2s;flex-shrink:0}
        .ip__close:hover{background:#013d97;transform:rotate(90deg)}
        .ip__logo{padding-bottom:20px;border-bottom:1px solid #f0f0f0}
        .ip__logo img{height:56px;width:auto;object-fit:contain;display:block}
        .ip__section{display:flex;flex-direction:column;gap:14px;padding-bottom:24px;border-bottom:1px solid #f0f0f0}
        .ip__section:last-child{border-bottom:none;padding-bottom:0}
        .ip__heading{font-size:17px;font-weight:700;color:#013d97;position:relative;padding-bottom:10px}
        .ip__heading::after{content:"";position:absolute;bottom:0;left:0;width:40px;height:3px;background:#029add;border-radius:2px}
        .ip__text{font-size:13.5px;color:#424244;line-height:1.7}
        .ip__contact{display:flex;flex-direction:column;gap:14px}
        .ip__contact-item{display:flex;align-items:flex-start;gap:14px}
        .ip__icon{width:38px;height:38px;border-radius:50%;background:#029add;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;flex-shrink:0}
        .ip__label{display:block;font-size:11px;font-weight:700;color:#029add;text-transform:uppercase;letter-spacing:.06em;margin-bottom:2px}
        .ip__value{display:block;font-size:13.5px;color:#013d97;font-weight:500;transition:color .2s}
        a.ip__value:hover{color:#029add}
        .ip__social{display:flex;gap:10px;flex-wrap:wrap}
        .ip__soc-link{width:38px;height:38px;border-radius:50%;background:#029add;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;transition:background .2s,transform .15s}
        .ip__soc-link:hover{background:#013d97;transform:translateY(-2px)}

        @media(max-width:1024px){
          .nb-nav__links,.nb-nav__cta,.nb-nav__info-btn,.nb-nav__icon-btn,.nb-acct{display:none}
          .nb-nav__shopnow{display:inline-flex}
          .nb-nav__booknow{display:inline-flex}
          .nb-nav__burger{display:flex}
          .nb-nav{justify-content:flex-end}
        }
        @media(max-width:700px){
          .nb-top{padding-left:0;padding-right:12px}
          .nb-top__left{gap:6px;padding-left:0}
          .nb-top__follow{display:none}
          .nb-top__info--static{display:none}
          .nb-top__email{display:none}
          .nb-top__phone{display:flex}
          .nb-logo{width:140px;padding:0 6px 0 8px}
          .nb-logo::after{right:-16px;width:32px}
          .nb-logo__img{height:80px;max-width:120px}
        }
        @media(max-width:480px){
          .nb-top__right .nb-top__info:not(.nb-top__phone){display:none}
          .nb-logo{width:110px}
          .nb-logo__img{height:70px;max-width:90px}
          .nb-nav__shopnow{padding:0 10px;font-size:12px}
          .nb-nav__booknow{padding:0 10px;font-size:12px}
        }

        a:focus-visible,button:focus-visible{outline:2px solid #029add;outline-offset:2px}
        @media(prefers-reduced-motion:reduce){*,*::before,*::after{transition-duration:0ms!important}}
      `}</style>
    </>
  );
}

export default Navbar;